import React, { lazy, memo, Suspense, useCallback, useEffect, useMemo, useRef } from 'react'

import { FiltersSidebar } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { bool, func } from 'prop-types'

import { FILTER_NAME } from 'Pods/SearchPage/Shared/Tracking/constants'
import { useTrackFilterSelect } from 'Pods/SearchPage/Shared/Tracking/FilterTracking'
import useTranslate from 'Pods/SearchPage/Shared/useTranslate'
import { setDatePublished } from 'Pods/SearchPage/Store/actions/datePublished'
import { DATE_PUBLISHED } from 'Pods/SearchPage/Store/constants'
import { useSearchPageStore } from 'Pods/SearchPage/Store/index'

import { NOOP } from 'Utils/stableDefaults'

import { datePublishedFiltersConfig } from './constants'

// Keeps react-day-picker out of the search pack. Safe to suspend: both mount paths (the
// FiltersWrapper dropdown and MoreFiltersDrawer) render their content only after a user
// interaction, so this boundary is never reached during the page's SSR pass.
const CustomRange = lazy(
  () => import(/* webpackChunkName: "search-custom-date-range" */ './Components/CustomRange/CustomRange')
)

function DatePublishedContent({ onApply = NOOP, autoApply = false, showApply = false }) {
  const translate = useTranslate()

  const datePublished = useSearchPageStore(state => state.datePublished ?? DATE_PUBLISHED.ALL_TIME)
  const trackFilterSelect = useTrackFilterSelect()

  // Selecting Custom replaces the preset before any range is applied; remember it so cancelling
  // the calendar does not leave an empty `custom` behind. Tracked off the store rather than off
  // the change handler because external writes (Clear all, the Reset button, popstate restore)
  // never pass through it, and a stale ref would resurrect a preset the user already dropped.
  const previousDatePublished = useRef(DATE_PUBLISHED.ALL_TIME)

  useEffect(() => {
    if (datePublished !== DATE_PUBLISHED.CUSTOM) {
      previousDatePublished.current = datePublished
    }
  }, [datePublished])

  // A custom range is reported by CustomRange once its dates are applied.
  const handleFilterChange = useCallback((_, value) => {
    if (value !== DATE_PUBLISHED.CUSTOM) {
      trackFilterSelect(FILTER_NAME.DATE_PUBLISHED, value)
    }
    setDatePublished(value)
  }, [trackFilterSelect])

  const handleCustomRangeCancel = useCallback(() => {
    setDatePublished(previousDatePublished.current)
  }, [])

  const selectedFilters = useMemo(() => [datePublished], [datePublished])

  const filters = useMemo(() => datePublishedFiltersConfig.map((group) => ({
    ...group,
    filters: group.filters.map(({ value, labelKey }) => ({
      value,
      label: translate(`search_page.filters.date_published.${labelKey}`),
    })),
  })), [])

  return (
    <>
      <FiltersSidebar
        isCollapsible={false}
        filters={filters}
        selectedFilters={selectedFilters}
        onFiltersChange={handleFilterChange}
        // limit=0 tells FiltersGroup to show all items (0 || initFilters.length = all)
        limit={0}
        useRadio
        hideLastDivider
        hideShowMore
        baseFormWrapperClassName="filter-wrapper__content"
      />
      {datePublished === DATE_PUBLISHED.CUSTOM && (
        <Suspense fallback={null}>
          <CustomRange
            onApply={onApply}
            onCancel={handleCustomRangeCancel}
            autoApply={autoApply}
            showApply={showApply}
          />
        </Suspense>
      )}
    </>
  )
}

DatePublishedContent.propTypes = {
  onApply: func,
  autoApply: bool,
  showApply: bool,
}

export default memo(DatePublishedContent)
